/**
 * Commerce · Promotion Engine (Wave 1).
 * 8 promo types + budget/expected/actual/ROAS/approval.
 * Auto-generates coupon codes. Uses RecordEditor for creation. Reuses existing primitives only.
 */
import { useMemo, useState } from "react";
import { Percent, DollarSign, Package, Gift, Truck, Zap, Ticket, Sparkles } from "lucide-react";
import { Card, SectionHeader, Button, TextInput } from "../../components/lib";
import { TabBar, Field, Drawer, Segmented } from "../../components/lib.overlay";
import { RecordEditor, FieldSpec } from "../../components/RecordEditor";
import { AuditTrail } from "../../components/AuditTrail";
import { PROMOS, Promo, PromoKind, PRODUCTS } from "../../mocks/commerceData";

const KIND_META: Record<PromoKind, { icon: any; label: string; tint: string; fg: string }> = {
  percentage:    { icon: Percent,   label: "Percentage",     tint: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  fixed:         { icon: DollarSign,label: "Fixed",          tint: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
  bundle:        { icon: Package,   label: "Bundle",         tint: "var(--ph-ai-tint)",      fg: "var(--ph-ai)"            },
  buy_x_get_y:   { icon: Zap,       label: "Buy X Get Y",    tint: "var(--ph-warning-tint)", fg: "var(--ph-warning)"       },
  free_shipping: { icon: Truck,     label: "Free shipping",  tint: "var(--ph-ai-tint)",      fg: "var(--ph-ai)"            },
  gift:          { icon: Gift,      label: "Gift",           tint: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)" },
  coupon:        { icon: Ticket,    label: "Coupon",         tint: "var(--ph-neutral-tint)", fg: "var(--ph-ink-strong)"    },
  automatic:     { icon: Sparkles,  label: "Automatic",      tint: "var(--ph-success-tint)", fg: "var(--ph-success)"       },
};

const STATUS_TINT: Record<Promo["status"], { bg: string; fg: string }> = {
  active:    { bg: "var(--ph-success-tint)", fg: "var(--ph-success)"      },
  scheduled: { bg: "var(--ph-brand-tint)",   fg: "var(--ph-brand-primary)"},
  expired:   { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-soft)"     },
  draft:     { bg: "var(--ph-neutral-tint)", fg: "var(--ph-ink-muted)"    },
  paused:    { bg: "var(--ph-warning-tint)", fg: "var(--ph-warning)"      },
};

function generateCouponCode(label?: string): string {
  const prefix = (label || "ORV").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 4) || "ORV";
  const suffix = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${prefix}-${suffix}`;
}

const productOptions = () => PRODUCTS.map((p) => ({ value: p.sku, label: `${p.sku} · ${p.name}` }));

const PROMO_SCHEMA: FieldSpec[] = [
  { key: "label",              label: "Promotion name",       kind: "text",     placeholder: "Spring Kick-off" },
  { key: "kind",               label: "Type",                 kind: "select", options: [
      { value: "percentage",    label: "Percentage discount" },
      { value: "fixed",         label: "Fixed amount" },
      { value: "bundle",        label: "Bundle" },
      { value: "buy_x_get_y",   label: "Buy X get Y" },
      { value: "free_shipping", label: "Free shipping" },
      { value: "gift",          label: "Gift with purchase" },
      { value: "coupon",        label: "Coupon" },
      { value: "automatic",     label: "Automatic (no code)" },
  ]},
  { key: "code",               label: "Coupon code",          kind: "text",   placeholder: "auto-generated" },
  { key: "discountValue",      label: "Discount value",       kind: "number", placeholder: "15" },
  { key: "productSkus",        label: "Products",             kind: "multiselect", options: productOptions() },
  { key: "collections",        label: "Collections (comma-separated)", kind: "text", placeholder: "eau-de-parfum, discovery" },
  { key: "buyQty",             label: "Buy qty (for BXGY)",   kind: "number", placeholder: "2" },
  { key: "getQty",             label: "Get qty (for BXGY)",   kind: "number", placeholder: "1" },
  { key: "giftSku",            label: "Gift SKU (for Gift)",  kind: "select", options: productOptions() },
  { key: "window",             label: "Window",               kind: "text",   placeholder: "2026-03-01 → 2026-03-31" },
  { key: "cap",                label: "Redemption cap",       kind: "number", placeholder: "500" },
  { key: "budgetEGP",          label: "Budget (EGP)",         kind: "number", placeholder: "50000" },
  { key: "expectedRevenueEGP", label: "Expected revenue (EGP)", kind: "number", placeholder: "300000" },
  { key: "expectedMarginPct",  label: "Expected margin %",    kind: "number", placeholder: "45" },
  { key: "status",             label: "Status",               kind: "select", options: [
      { value: "draft",     label: "Draft" },
      { value: "scheduled", label: "Scheduled" },
      { value: "active",    label: "Active" },
      { value: "paused",    label: "Paused" },
      { value: "expired",   label: "Expired" },
  ]},
  { key: "approvalStatus",     label: "Approval",             kind: "select", options: [
      { value: "draft",    label: "Draft" },
      { value: "pending",  label: "Pending" },
      { value: "approved", label: "Approved" },
      { value: "rejected", label: "Rejected" },
  ]},
  { key: "autoApply",          label: "Auto-apply",           kind: "toggle" },
];

export default function Promotions() {
  const [promos, setPromos] = useState<Promo[]>(PROMOS);
  const [tab, setTab] = useState<Promo["status"] | "all">("all");
  const [q, setQ] = useState("");
  const [editorOpen, setEditorOpen] = useState<"create" | "edit" | null>(null);
  const [selected, setSelected] = useState<Promo | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const counts = useMemo(() => {
    const c: Record<string, number> = { all: promos.length };
    promos.forEach((p) => { c[p.status] = (c[p.status] || 0) + 1; });
    return c;
  }, [promos]);

  const filtered = useMemo(
    () => promos.filter((p) =>
      (tab === "all" || p.status === tab) &&
      (!q || p.label.toLowerCase().includes(q.toLowerCase()) || p.code.toLowerCase().includes(q.toLowerCase())),
    ),
    [promos, tab, q],
  );

  const openCreate = () => {
    setSelected(null);
    setEditorOpen("create");
  };

  const openEdit = (p: Promo) => {
    setSelected(p);
    setDrawerOpen(false);
    setEditorOpen("edit");
  };

  const openDrawer = (p: Promo) => {
    setSelected(p);
    setDrawerOpen(true);
  };

  const initialPayload = () => ({
    code: generateCouponCode(),
    kind: "percentage" as PromoKind,
    status: "draft",
    approvalStatus: "draft",
    redemptions: 0,
    revenueEGP: 0,
    productSkus: [],
    autoApply: false,
  });

  return (
    <div className="space-y-6" data-testid="cm-promotions">
      <Card testId="cm-promo-filters">
        <div className="flex items-center gap-3">
          <div className="flex-1"><TextInput value={q} onChange={setQ} placeholder="Search promos or coupons…" testId="cm-promo-search" /></div>
          <Button testId="cm-promo-add" onClick={openCreate}>+ New promotion</Button>
        </div>
        <div className="mt-4">
          <TabBar<Promo["status"] | "all">
            testId="cm-promo-tabs"
            active={tab}
            onChange={setTab}
            tabs={[
              { key: "all",       label: "All",       count: counts.all       },
              { key: "active",    label: "Active",    count: counts.active    || 0 },
              { key: "scheduled", label: "Scheduled", count: counts.scheduled || 0 },
              { key: "draft",     label: "Draft",     count: counts.draft     || 0 },
              { key: "paused",    label: "Paused",    count: counts.paused    || 0 },
              { key: "expired",   label: "Expired",   count: counts.expired   || 0 },
            ]}
          />
        </div>
      </Card>

      <Card padded={false} testId="cm-promo-list-card">
        <div className="p-5" style={{ borderBottom: "1px solid var(--ph-border-subtle)" }}>
          <SectionHeader eyebrow="Promotions" title={`${filtered.length} promotion${filtered.length === 1 ? "" : "s"}`} />
        </div>
        <div className="p-2" data-testid="cm-promo-list">
          {filtered.map((p) => {
            const kind = p.kind || "coupon";
            const meta = KIND_META[kind];
            const Icon = meta.icon;
            const st = STATUS_TINT[p.status];
            const roas = p.roas ?? (p.budgetEGP && p.actualRevenueEGP ? p.actualRevenueEGP / p.budgetEGP : 0);
            return (
              <button
                key={p.id}
                data-testid={`cm-promo-${p.id}`}
                onClick={() => openDrawer(p)}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 hover:bg-[var(--ph-surface-hover)]"
                style={{ borderRadius: "var(--ph-radius-sm)" }}
              >
                <span className="inline-flex items-center justify-center flex-shrink-0"
                  style={{ width: 28, height: 28, borderRadius: "999px", background: meta.tint, color: meta.fg }}>
                  <Icon size={13} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="inline-flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold" style={{ color: "var(--ph-ink-strong)" }}>{p.label}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-mono" style={{ background: "var(--ph-neutral-tint)", color: "var(--ph-ink-strong)", borderRadius: "var(--ph-radius-sm)" }}>{p.code}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: meta.tint, color: meta.fg, borderRadius: "var(--ph-radius-sm)" }}>{meta.label}</span>
                    <span className="px-1.5 py-0.5 text-[9px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: st.bg, color: st.fg, borderRadius: "var(--ph-radius-sm)" }}>{p.status}</span>
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--ph-ink-muted)" }}>
                    {p.discount} · {p.window} · {p.redemptions} redeemed{p.cap ? ` / ${p.cap}` : ""}
                    {roas > 0 ? ` · ROAS ${roas.toFixed(2)}×` : ""}
                  </div>
                </div>
                <div className="text-right text-xs tabular-nums" style={{ color: "var(--ph-ink-muted)" }}>
                  EGP {(p.actualRevenueEGP ?? p.revenueEGP).toLocaleString()}
                </div>
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="text-sm text-center py-8" style={{ color: "var(--ph-ink-muted)" }}>No promotions match your filter.</div>
          )}
        </div>
      </Card>

      <Drawer open={drawerOpen && Boolean(selected)} onClose={() => setDrawerOpen(false)} title="Promotion 360" testId="cm-promo-drawer" width={600}>
        {selected && (() => {
          const kind = selected.kind || "coupon";
          const meta = KIND_META[kind];
          const st = STATUS_TINT[selected.status];
          const roas = selected.roas ?? (selected.budgetEGP && selected.actualRevenueEGP ? selected.actualRevenueEGP / selected.budgetEGP : 0);
          return (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-mono px-1.5 py-0.5" style={{ background: "var(--ph-neutral-tint)", color: "var(--ph-ink-strong)", borderRadius: "var(--ph-radius-sm)" }}>{selected.code}</span>
                <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: meta.tint, color: meta.fg, borderRadius: "var(--ph-radius-sm)" }}>{meta.label}</span>
                <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase" style={{ letterSpacing: "0.14em", background: st.bg, color: st.fg, borderRadius: "var(--ph-radius-sm)" }}>{selected.status}</span>
              </div>
              <div style={{ fontFamily: "var(--ph-display-font)", fontSize: "1.35rem", color: "var(--ph-ink-strong)" }} className="mb-4">{selected.label}</div>

              <Field label="Discount"           inline><span className="text-sm">{selected.discount}</span></Field>
              <Field label="Window"             inline><span className="text-sm">{selected.window}</span></Field>
              <Field label="Redemptions"        inline><span className="text-sm tabular-nums">{selected.redemptions}{selected.cap ? ` / ${selected.cap}` : ""}</span></Field>
              <Field label="Products"           inline><span className="text-sm">{selected.productSkus?.length ? selected.productSkus.join(", ") : "—"}</span></Field>
              <Field label="Collections"        inline><span className="text-sm">{selected.collections?.length ? selected.collections.join(", ") : "—"}</span></Field>
              <Field label="Budget"             inline><span className="text-sm tabular-nums">EGP {(selected.budgetEGP || 0).toLocaleString()}</span></Field>
              <Field label="Expected revenue"   inline><span className="text-sm tabular-nums">EGP {(selected.expectedRevenueEGP || 0).toLocaleString()}</span></Field>
              <Field label="Expected margin"    inline><span className="text-sm tabular-nums">{selected.expectedMarginPct != null ? `${selected.expectedMarginPct}%` : "—"}</span></Field>
              <Field label="Actual revenue"     inline><span className="text-sm tabular-nums">EGP {(selected.actualRevenueEGP ?? selected.revenueEGP).toLocaleString()}</span></Field>
              <Field label="ROAS"               inline><span className="text-sm tabular-nums">{roas > 0 ? `${roas.toFixed(2)}×` : "—"}</span></Field>
              <Field label="Approval"           inline><span className="text-sm">{selected.approvalStatus || "—"}{selected.approvedBy ? ` · by ${selected.approvedBy}` : ""}</span></Field>
              <Field label="Auto-apply"         inline><span className="text-sm">{selected.autoApply ? "Yes" : "No"}</span></Field>

              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-[0.14em] mb-1" style={{ color: "var(--ph-ink-muted)" }}>Audit</div>
                <AuditTrail collection="promos" recordId={selected.id} testId="cm-promo-audit" />
              </div>

              <div className="pt-4 flex justify-end gap-2">
                <Button variant="secondary" testId="cm-promo-regen" onClick={() => {
                  const next = { ...selected, code: generateCouponCode(selected.label) };
                  setSelected(next);
                  setPromos((list) => list.map((r) => r.id === next.id ? next : r));
                }}>Regenerate code</Button>
                <Button testId="cm-promo-edit" onClick={() => openEdit(selected)}>Edit</Button>
              </div>
            </div>
          );
        })()}
      </Drawer>

      <RecordEditor<Promo>
        open={editorOpen !== null}
        onClose={() => setEditorOpen(null)}
        mode={editorOpen === "edit" ? "edit" : "create"}
        title={editorOpen === "edit" ? "Edit promotion" : "New promotion"}
        collection="promos"
        schema={PROMO_SCHEMA}
        initial={editorOpen === "edit" && selected ? (selected as unknown as Record<string, unknown>) : (initialPayload() as unknown as Record<string, unknown>)}
        onSaved={(row) => {
          const saved = row as unknown as Promo;
          setPromos((list) => {
            const idx = list.findIndex((r) => r.id === saved.id);
            if (idx >= 0) { const copy = [...list]; copy[idx] = saved; return copy; }
            return [saved, ...list];
          });
        }}
        testId="cm-promo-editor"
      />
    </div>
  );
}
